import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { ResponsiveAdminLayout } from '../components/ResponsiveAdminLayout';
import { Pagination } from '../components/Pagination';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { AppColors } from '../theme/colors';
import { AuthService } from '../services/authService';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';

interface AdminUser {
  id: string;
  userType: string;
  displayName: string;
  email: string;
  department: string;
  role: string;
  createdAt: Date;
  isActive: boolean;
}

export const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  
  // ページネーション用の状態
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // フォーム状態
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // ダイアログ状態
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (company?.id) {
      fetchAdminUsers();
    }
  }, [company?.id]);

  const fetchAdminUsers = async () => {
    if (!company?.id) return;
    
    setLoading(true);
    try {
      // Firestoreから管理者ユーザーを取得
      const q = query(
        collection(db, 'users'),
        where('userType', 'in', ['admin', 'superadmin']),
        where('companyId', '==', company.id)
      );
      
      const querySnapshot = await getDocs(q);
      const admins = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as AdminUser[];
      
      setAdminUsers(admins);
    } catch (error) {
      console.error('管理者ユーザー取得エラー:', error);
      setSnackbar({
        open: true,
        message: '管理者ユーザーの取得に失敗しました',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      navigate('/admin-login');
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.name.trim()) {
      errors.name = '管理者名を入力してください';
    } else if (formData.name.length > 15) {
      errors.name = '管理者名は15字以内で入力してください';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      errors.email = '有効なメールアドレスを入力してください';
    } else if (
      // 同じ会社に同じメールの管理者を重ねて登録しない（#28）。重複すると、システム管理者が
      // 「管理者」として入ってしまうことがある。サーバー（createCompanyAdmin）でも止めるが、
      // 一覧は読み込み済みなので、ここで先に気づけるようにする
      adminUsers.some(
        (admin) => admin.email?.trim().toLowerCase() === formData.email.trim().toLowerCase()
      )
    ) {
      errors.email = 'このメールアドレスの管理者はすでに登録されています';
    }
    
    // 部署は任意。会社登録の画面でも「部署名（任意）」で、サーバー側も空を受け付ける
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    if (!user?.uid) {
      setSnackbar({
        open: true,
        message: '認証が必要です。再度ログインしてください。',
        severity: 'error',
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      // ランダムな仮パスワードを生成
      const tempPassword = generateRandomPassword();
      
      // 管理者アカウント作成
      await AuthService.createAdminAccount({
        email: formData.email,
        password: tempPassword,
        displayName: formData.name,
        userType: 'admin',
        department: formData.department,
        role: '管理者',
        companyId: company?.id,
        companyName: company?.companyName,
        currentUserId: user.uid, // ✅ 現在のユーザー ID を渡す
      });
      
      // ✅ パスワードリセットメールを送信（同じメールアドレスでも1回のみ送信）
      try {
        if (company?.corporateCode) {
          await AuthService.sendPasswordResetEmail(company.corporateCode, formData.email);
        }
      } catch (emailError) {
        console.warn('パスワードリセットメール送信失敗:', emailError);
        // メール送信失敗してもアカウント作成は成功しているので継続
      }
      
      setSnackbar({
        open: true,
        message: '管理者を追加しました。パスワードリセットメールを送信しました。',
        severity: 'success',
      });
      
      // フォームをクリア
      setFormData({ name: '', email: '', department: '' });
      setFormErrors({});
      
      // 管理者一覧を再取得
      await fetchAdminUsers();
    } catch (error: any) {
      console.error('管理者追加エラー:', error);
      setSnackbar({
        open: true,
        message: error.message || '管理者の追加に失敗しました',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (admin: AdminUser) => {
    setDeleteTarget(admin);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    
    try {
      // 自分自身を削除しようとしていないかチェック
      if (user?.uid === deleteTarget.id) {
        setSnackbar({
          open: true,
          message: '自分自身を削除することはできません。',
          severity: 'error',
        });
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
        return;
      }

      // 権限と会社の一致は deleteUserFromAuth(Cloud Functions)がIDトークンを元に
      // 検証する。ここで先読みして確認する必要はない。
      // （削除直後に再度この画面から読むと、存在しないドキュメントの読み取りとなり
      //   紛らわしいエラーになっていた）

      if (!user?.uid) {
        throw new Error('認証が必要です。再度ログインしてください。');
      }

      // Cloud Function 経由で削除（Auth + Firestore）
      await AuthService.deleteUserFromAuth(deleteTarget.id, user.uid);

      
      setSnackbar({
        open: true,
        message: '管理者を削除しました',
        severity: 'success',
      });
      
      // 管理者一覧を再取得
      await fetchAdminUsers();
    } catch (error: any) {
      console.error('管理者削除エラー:', error);
      
      // エラーメッセージを日本語で表示
      let errorMessage = '管理者の削除に失敗しました。しばらく時間をおいてから再度お試しください';
      
      if (error.message?.includes('権限がありません')) {
        errorMessage = 'ユーザー削除の権限がありません。管理者にお問い合わせください';
      } else if (error.message?.includes('他社のユーザー')) {
        errorMessage = '他社のユーザーは削除できません';
      } else if (error.message?.includes('ネットワーク')) {
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください';
      } else if (error.message?.includes('見つかりません')) {
        errorMessage = '削除対象のユーザーが見つかりません。ページを更新してから再度お試しください';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  // ランダムパスワード生成
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // ページネーション計算
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return adminUsers.slice(startIndex, endIndex);
  }, [adminUsers, currentPage, itemsPerPage]);
  
  const totalPages = Math.ceil(adminUsers.length / itemsPerPage);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // 管理者権限チェック（Flutter版と同じ会社IDベース）
  if (!user || (!user.userType || (user.userType !== 'admin' && user.userType !== 'superadmin'))) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          管理者権限が必要です。
        </Typography>
        <Button onClick={() => navigate('/admin')}>管理者画面に戻る</Button>
      </Box>
    );
  }

  if (authLoading || companyLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ResponsiveAdminLayout pageTitle="ユーザー管理">
      <Box>

        {/* 基準の余白の状態: 内側コンテナ（PCは中央寄せ・最大幅） */}
        <Box sx={{ maxWidth: isDesktop ? '1000px' : '100%', mx: isDesktop ? 'auto' : 0, width: '100%' }}>
        

        {/* コンテンツエリア */}
        <Box sx={{ p: 2 }}>
        {/* 新しい管理者を追加 */}
        <Card sx={{ mb: 3, borderRadius: 3, border: `1px solid ${AppColors.border}` }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'black',
                mb: 2.5,
              }}
            >
              新しい管理者を追加
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="管理者名"
                placeholder="管理者の名前を入力してください"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!formErrors.name}
                helperText={formErrors.name || `${formData.name.length}/15`}
                FormHelperTextProps={{
                  sx: { textAlign: 'right' }
                }}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />,
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '1rem',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: AppColors.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: AppColors.primary,
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="メールアドレス"
                placeholder="管理者のメールアドレスを入力してください"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={!!formErrors.email}
                helperText={formErrors.email}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />,
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '1rem',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: AppColors.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: AppColors.primary,
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="部署（任意）"
                placeholder="管理者の部署を入力してください"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                error={!!formErrors.department}
                helperText={formErrors.department}
                InputProps={{
                  startAdornment: <BusinessIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />,
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '1rem',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: AppColors.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: AppColors.primary,
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Box>
            
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                py: 2,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${AppColors.primary} 0%, ${AppColors.primaryLight} 100%)`,
                color: 'white', // Flutter版と同じ白色の文字
                '&:hover': {
                  background: `linear-gradient(135deg, ${AppColors.primaryDark} 0%, ${AppColors.primary} 100%)`,
                  color: 'white', // ホバー時も白色を維持
                },
                '&:disabled': {
                  background: `linear-gradient(135deg, ${AppColors.primary} 0%, ${AppColors.primaryLight} 100%)`,
                  color: 'white', // 無効時も白色を維持
                },
              }}
            >
              {isSubmitting ? <CircularProgress size={20} color="inherit" /> : '管理者を追加'}
            </Button>
          </CardContent>
        </Card>

        {/* 管理者一覧 */}
        <Card sx={{ borderRadius: 3, border: `1px solid ${AppColors.border}` }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'black',
                mb: 2,
              }}
            >
              管理者一覧
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : adminUsers.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <PeopleIcon sx={{ fontSize: 48, color: AppColors.textSecondary, mb: 2 }} />
                <Typography sx={{ color: AppColors.textSecondary, fontSize: 16 }}>
                  管理者が登録されていません
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {paginatedUsers.map((admin) => (
                  <Box
                    key={admin.id}
                    sx={{
                      p: 2,
                      bgcolor: admin.isActive ? 'white' : AppColors.surface,
                      borderRadius: 2,
                      border: `1px solid ${admin.isActive ? AppColors.border : AppColors.border + '80'}`,
                    }}
                  >
                    {/* 一番上の行: 名前、管理者タグ、削除ボタン */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: admin.isActive ? AppColors.textPrimary : AppColors.textSecondary,
                            mr: 1,
                          }}
                        >
                          {admin.displayName}
                        </Typography>
                        <Chip
                          label={admin.userType === 'superadmin' ? 'システム管理者' : '管理者'}
                          size="small"
                          sx={{
                            bgcolor: admin.userType === 'superadmin' ? AppColors.accent : AppColors.primary,
                            color: 'white',
                            fontSize: 11,
                            height: 20,
                          }}
                        />
                      </Box>
                      
                      {/* 削除ボタンを一番上の行に配置 */}
                      {admin.userType === 'admin' && user?.uid !== admin.id && (
                        <IconButton
                          onClick={() => handleDeleteClick(admin)}
                          sx={{ color: AppColors.error }}
                          title="管理者を削除"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    
                    {/* その他の情報 */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: AppColors.textSecondary,
                          mb: 0.5,
                          wordBreak: 'break-all',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {admin.email}
                      </Typography>
                      {/* 部署は任意のため、空のときは行ごと出さない（空の行が残らないように） */}
                      {admin.department && (
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: AppColors.textSecondary,
                            mb: 0.5,
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {admin.department}
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: AppColors.textSecondary,
                        }}
                      >
                        登録日: {formatDate(admin.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            
            {/* ページネーション */}
            {adminUsers.length > itemsPerPage && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                py: 3,
                bgcolor: 'white',
                borderTop: `1px solid ${AppColors.border}`,
                mx: -2.5, // CardContentのパディング分だけ左右に拡張
                px: 2.5,  // 内容は元のパディングを維持
                mt: 2,    // 上の空白を追加
              }}>
                <Typography variant="body2" sx={{ color: AppColors.textSecondary, mb: 2 }}>
                  {adminUsers.length}件中 {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, adminUsers.length)}件を表示
                </Typography>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>管理者を削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget?.displayName} を管理者から削除しますか？
            <br />
            この操作は取り消せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            style={{ color: AppColors.error }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            backgroundColor: snackbar.severity === 'success' ? AppColors.success : AppColors.error,
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </Box>

    </ResponsiveAdminLayout>
  );
};
